import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { evaluate, parseBuildNumber, parseManifest, type VersionState } from './versionState'

const DISMISSED_BUILD_STORAGE_KEY = 'hub-version-dismissed-build'

export interface AppVersionContextValue {
  state: VersionState
  latestBuild: number | null
  showStaleNotice: boolean
  dismiss: () => void
  reload: () => void
}

const INERT_VALUE: AppVersionContextValue = {
  state: 'current',
  latestBuild: null,
  showStaleNotice: false,
  dismiss: () => {},
  reload: () => {},
}

const AppVersionContext = createContext<AppVersionContextValue | null>(null)

// Every sessionStorage access is wrapped in try/catch: a throwing or absent
// storage (private browsing, hardened browser settings, etc.) must never
// break the app.
function readDismissedBuild(): number | null {
  try {
    const raw = sessionStorage.getItem(DISMISSED_BUILD_STORAGE_KEY)
    return raw === null ? null : parseBuildNumber(raw)
  } catch {
    return null
  }
}

function writeDismissedBuild(build: number): void {
  try {
    sessionStorage.setItem(DISMISSED_BUILD_STORAGE_KEY, String(build))
  } catch {
    // Ignore - the in-memory dismissal still applies for this session.
  }
}

export interface AppVersionProviderProps {
  children: ReactNode
  myBuild?: number | null
  pollIntervalMs?: number
  reload?: () => void
}

export function AppVersionProvider({
  children,
  myBuild = parseBuildNumber(import.meta.env.VITE_BUILD_NUMBER),
  pollIntervalMs = 300_000,
  reload = () => window.location.reload(),
}: AppVersionProviderProps) {
  const [state, setState] = useState<VersionState>('current')
  const [latestBuild, setLatestBuild] = useState<number | null>(null)
  const [dismissedBuild, setDismissedBuild] = useState<number | null>(() => readDismissedBuild())

  const poll = useCallback(async () => {
    let manifest = null
    try {
      const res = await fetch('/version.json', { cache: 'no-store' })
      if (res.ok) {
        const body = await res.json()
        manifest = parseManifest(body)
      }
    } catch {
      manifest = null
    }

    setState((prev) => evaluate(myBuild, manifest, prev))
    if (manifest) {
      setLatestBuild(manifest.build)
    }
  }, [myBuild])

  useEffect(() => {
    void poll()

    const interval = setInterval(() => {
      void poll()
    }, pollIntervalMs)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void poll()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [poll, pollIntervalMs])

  const dismiss = useCallback(() => {
    if (latestBuild === null) return
    setDismissedBuild(latestBuild)
    writeDismissedBuild(latestBuild)
  }, [latestBuild])

  const showStaleNotice = state === 'stale' && latestBuild !== dismissedBuild

  const value: AppVersionContextValue = {
    state,
    latestBuild,
    showStaleNotice,
    dismiss,
    reload,
  }

  return <AppVersionContext.Provider value={value}>{children}</AppVersionContext.Provider>
}

export function useAppVersion(): AppVersionContextValue {
  const ctx = useContext(AppVersionContext)
  return ctx ?? INERT_VALUE
}
